import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useRueckgaengig } from "../design/Rueckgaengig";
import { useDokument, useSammlung, type Zeile } from "../plattform/lesemodelle";
import { startbeitragRegistrieren, type Kernaktion, type Startlage, type Startziel } from "../plattform/startansicht";
import { beobachten, eintraege, type Eintrag } from "../plattform/warteschlange";
import { personAus, useMeineAufgaben } from "./daten";
import { eingehenderNotruf } from "./eingehend";
import { standErreichtMelden, treibergruppeMelden } from "./meldungen";

/**
 * Ziele des Kontexts Lage auf der Startansicht (oberflaeche.md §3.1 V-12, §4.3; design/README §10.2). Rang = Zeile der
 * Rolle in §4.3; die kleinste Zeile mit Hauptaktion steht an erster Stelle in `main`, die übrigen Kernaktionen direkt in
 * `navigation` „Drückjagd“.
 *
 * - Schütze, Standschnaller: „Stand erreicht“ (Hauptaktion vor Beginn), solange der zugewiesene Stand nicht erreicht ist.
 * - Ansteller: „Abholliste“ (Hauptaktion in jeder Phase).
 * - Treiber, Treiberführer: „Wildsichtung“ (Hauptaktion während der Jagd); Treiberführer „am Start“ vor Beginn,
 *   „am Sammelpunkt“ danach, während der Jagd beide direkt.
 * - Hundeführer mit eigenem Hund im Treiben: „Hund vermisst“ (Hauptaktion des Durchgeh-Hundeführers während der Jagd),
 *   „Hund zurück“ bei offener eigener Vermisstmeldung (Hauptaktion danach); der Jagdleiter „Hund zurück“ für jede.
 * - Durchgeh-Hundeführer: „Hunde im Treiben“ vor Beginn und danach ohne offene eigene Vermisstmeldung; während der Jagd
 *   als Rückfall, wenn „Hund vermisst“ nicht angeboten ist (kein eigener Hund im Treiben oder alle vermisst).
 * - Nachsuchenführer: „Hunde im Treiben“ vor Beginn; während und danach als Rückfall ohne zugewiesene Suche (sonst
 *   steht der Suchbereich in `main`, Kontext Schuss).
 * - Jagdherr, Jagdleiter: „Lagebild“ (Hauptaktion in jeder Phase).
 * - Alle: „Hund gesichtet“ als dringendes weiteres Ziel, solange eine Vermisstmeldung offen ist; „Wildsichtung“ als
 *   weiteres Ziel, wo sie nicht Hauptaktion ist (F-130: Treiber, Ansteller, Hundeführer, Schütze).
 * - Jagdherr, Jagdleiter: Geht ein Notruf ein, den noch niemand quittiert hat, wechselt die Startansicht ins Lagebild,
 *   das ihn als eingehenden Notruf zeigt (oberflaeche.md §5).
 */
const RANG = { Schütze: 0, Ansteller: 1, Treiber: 2, Treiberführer: 3, "Durchgeh-Hundeführer": 4, Nachsuchenführer: 5, Leitung: 6 } as const;
const HUNDEFUEHRENDE = ["Durchgeh-Hundeführer", "Standschnaller", "Nachsuchenführer"];
const WILDSICHTUNG_ROLLEN = ["Treiber", "Treiberführer", "Ansteller", "Schütze", "Standschnaller", ...HUNDEFUEHRENDE];
const KEINE: ReadonlySet<string> = new Set();

/** Eingehender Notruf der Leitung: die Startansicht übergibt an das Lagebild. */
function useEingehenderNotrufOeffnen(drueckjagdId: string, leitung: boolean, ich: string | null): void {
  const navigate = useNavigate();
  const notrufe = useSammlung(leitung ? `drueckjagden/${drueckjagdId}/offene_notrufe` : null).daten;
  const eingehend = leitung ? eingehenderNotruf(notrufe, ich, KEINE)?._id ?? null : null;
  useEffect(() => {
    if (eingehend) navigate(`/drueckjagd/${drueckjagdId}/lagebild`);
  }, [eingehend, drueckjagdId, navigate]);
}

/** Meldungen dieses Geräts in der Warteschlange (noch nicht oder schon übertragen, nicht abgewiesen); `null`, bis gelesen. */
function useEigeneMeldungen(drueckjagdId: string): Eintrag[] | null {
  const [liste, setListe] = useState<Eintrag[] | null>(null);
  useEffect(() => {
    let aktiv = true;
    const lesen = () => void eintraege().then((alle) => {
      if (aktiv) setListe(alle.filter((e) => e.daten["Drückjagd"] === drueckjagdId && e.stand !== "abgewiesen"));
    });
    lesen();
    const abmelden = beobachten(lesen);
    return () => { aktiv = false; abmelden(); };
  }, [drueckjagdId]);
  return liste;
}

function useLagebeitrag(lage: Startlage) {
  const { drueckjagdId: id, rollen, revier, phase } = lage;
  const rg = useRueckgaengig();
  const aufgaben = useMeineAufgaben(id);
  const besetzung = useDokument(revier && aufgaben.standId ? `drueckjagden/${id}/reviere/${revier}/standbesetzung/${aufgaben.standId}` : null);
  const hundeStand = useSammlung(`drueckjagden/${id}/hunde_im_treiben`);
  const vermisstStand = useSammlung(`drueckjagden/${id}/vermisste_hunde`);
  const hunde = hundeStand.daten;
  const vermisst = vermisstStand.daten;
  const gelesen = useEigeneMeldungen(id);
  const eigene = gelesen ?? [];
  // erst wenn alles gelesen ist, entscheidet die Startansicht über einen Rückfall der Hauptaktion (plattform/startansicht.ts)
  const bereit = gelesen !== null && !aufgaben.laedt && !besetzung.laedt && !hundeStand.laedt && !vermisstStand.laedt;
  const pfad = (teil: string) => `/drueckjagd/${id}/${teil}`;
  const ich = aufgaben.person?.Kennung ?? null;
  const kernaktionen: Kernaktion[] = [];
  const weitere: Startziel[] = [];
  const hat = (...r: string[]) => r.some((x) => rollen.includes(x));
  useEingehenderNotrufOeffnen(id, hat("Jagdherr", "Jagdleiter"), ich);

  // Stand erreicht: ein Fingertipp für den zugewiesenen Stand, solange er nicht erreicht ist (F-112, F-113).
  const status = besetzung.daten?.["Anstellstatus"] as string | null | undefined;
  const gemeldet = eigene.some((e) => e.befehl === "StandErreichtMelden" && e.daten["Stand"] === aufgaben.standId);
  if (hat("Schütze", "Standschnaller") && aufgaben.standId && !gemeldet && (status === undefined || status === null || status === "offen" || status === "angestellt")) {
    const standId = aufgaben.standId;
    kernaktionen.push({
      begriff: "Stand erreicht", symbol: "stand-erreicht", pfad: pfad(""), rang: RANG.Schütze, haupt: phase === "vor Beginn",
      ausloesen: () => void standErreichtMelden(rg, id, standId, aufgaben.standnummer),
    });
  }

  if (hat("Ansteller")) kernaktionen.push({ begriff: "Abholliste", symbol: "ansteller", pfad: pfad("abholliste"), rang: RANG.Ansteller, haupt: true, weiteresZiel: true });

  // Wildsichtung: Hauptaktion der Treiber während der Jagd, sonst ein weiteres Ziel (F-130).
  const wildsichtungHaupt = phase === "während" && hat("Treiber", "Treiberführer");
  if (wildsichtungHaupt) {
    kernaktionen.push({ begriff: "Wildsichtung", symbol: "wildsichtung", pfad: pfad("wildsichtung"), rang: hat("Treiber") ? RANG.Treiber : RANG.Treiberführer, haupt: true, weiteresZiel: true });
  } else if (hat(...WILDSICHTUNG_ROLLEN)) {
    weitere.push({ begriff: "Wildsichtung", symbol: "wildsichtung", pfad: pfad("wildsichtung") });
  }

  // Treibergruppe am Start / am Sammelpunkt: der Treiberführer seiner Gruppe; was das Gerät schon gemeldet hat, entfällt.
  if (hat("Treiberführer") && aufgaben.treibergruppe && revier) {
    const gruppe = aufgaben.treibergruppe;
    const gemeldetAls = (befehl: string) => eigene.some((e) => e.befehl === befehl && e.daten["Treibergruppe"] === gruppe);
    const amStart = gemeldetAls("TreibergruppeAmStartMelden");
    const amSammelpunkt = gemeldetAls("TreibergruppeAmSammelpunktMelden");
    if (phase !== "danach" && !amStart && !amSammelpunkt) {
      kernaktionen.push({
        begriff: "am Start", symbol: "am-start", pfad: pfad(""), rang: RANG.Treiberführer, haupt: phase === "vor Beginn",
        ausloesen: () => void treibergruppeMelden(rg, id, revier, gruppe, "am Start"),
      });
    }
    if (phase !== "vor Beginn" && !amSammelpunkt) {
      kernaktionen.push({
        begriff: "am Sammelpunkt", symbol: "am-sammelpunkt", pfad: pfad(""), rang: RANG.Treiberführer, haupt: phase === "danach",
        ausloesen: () => void treibergruppeMelden(rg, id, revier, gruppe, "am Sammelpunkt"),
      });
    }
  }

  // Hund vermisst / Hund zurück (F-174): eigene Hunde im Treiben; „zurück“ auch der Jagdleiter für jeden vermissten Hund.
  const fuehrerVon = (d: Zeile, feld: string) => personAus(d[feld])?.Kennung ?? null;
  const offen = vermisst.filter((v) => v["Vermisststatus"] !== "zurück");
  const eigeneHunde = hunde.filter((h) => ich && fuehrerVon(h, "Führer") === ich);
  const eigeneVermisst = offen.filter((v) => ich && fuehrerVon(v, "Hundeführer") === ich);
  const vermissteNamen = new Set(eigeneVermisst.map((v) => String(v["Hund"])));
  const hundRang = hat("Durchgeh-Hundeführer") ? RANG["Durchgeh-Hundeführer"] : RANG.Nachsuchenführer;
  if (eigeneHunde.some((h) => !vermissteNamen.has(String(h["Hund"])))) {
    kernaktionen.push({
      begriff: "Hund vermisst", symbol: "hund-vermisst", pfad: pfad("hund-vermisst"), rang: hundRang,
      haupt: phase === "während" && hat("Durchgeh-Hundeführer"),
    });
  }
  const jagdleiter = hat("Jagdleiter");
  if (eigeneVermisst.length || (jagdleiter && offen.length)) {
    kernaktionen.push({
      begriff: "Hund zurück", symbol: "hund-zurueck", pfad: pfad("hund-zurueck"), rang: eigeneVermisst.length ? hundRang : RANG.Leitung,
      haupt: phase === "danach" && hat("Durchgeh-Hundeführer") && eigeneVermisst.length > 0,
    });
  }
  if (offen.length) weitere.push({ begriff: "Hund gesichtet", symbol: "hund-gesichtet", pfad: pfad("hund-gesichtet"), vorn: true });

  // Hunde im Treiben (§4.3): nur als Hauptaktion; sonst steht das Ziel bei den Zielen der Rolle (drueckjagd/ziele.ts)
  const hundeImTreiben = (rang: number, rueckfall: boolean): Kernaktion => ({
    begriff: "Hunde im Treiben", symbol: "hund", pfad: pfad("hunde"), rang, haupt: true, nurAlsHaupt: true, ...(rueckfall ? { rueckfall } : {}),
  });
  if (hat("Durchgeh-Hundeführer")) {
    const eigentlich = phase === "vor Beginn" || (phase === "danach" && bereit && eigeneVermisst.length === 0);
    kernaktionen.push(hundeImTreiben(RANG["Durchgeh-Hundeführer"], !eigentlich));
  }
  if (hat("Nachsuchenführer")) kernaktionen.push(hundeImTreiben(RANG.Nachsuchenführer, phase !== "vor Beginn"));

  if (hat("Jagdherr", "Jagdleiter")) kernaktionen.push({ begriff: "Lagebild", symbol: "lagebild", pfad: pfad("lagebild"), rang: RANG.Leitung, haupt: true, weiteresZiel: true });

  return { kernaktionen, weitere, bereit };
}

startbeitragRegistrieren(useLagebeitrag);
